import { useAction, useAtom } from "@reatom/npm-react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { MultiValue, SingleValue } from "react-select";
import Async from "react-select/async";
import { SELECT_STYLES } from "src/constants/selectStyle";
import {
  BrandOption,
  loadBrandsWithIdsAtom,
  loadBrandsWithIdsDebounceAction,
} from "src/store/atoms";
import { deepEqual } from "src/util";
import { isMultiValueSelect } from "src/util/isMultiValueSelect";

interface AsyncBrandSelectProps {
  value?: number[] | null;
  onChange: (value: number[] | null) => void;
  placeholder?: string;
  isLoading?: boolean;
}

const LOADING_MESSAGE = "Загрузка...";

export const AsyncBrandSelect = ({
  value,
  onChange,
  placeholder,
  isLoading,
}: AsyncBrandSelectProps) => {
  const [brands] = useAtom(loadBrandsWithIdsAtom.dataAtom);
  const loadBrands = useAction(loadBrandsWithIdsAtom);
  const loadBrandsWithIdsDebounce = useAction(loadBrandsWithIdsDebounceAction);

  const prevIdsRef = useRef<number[]>([]);

  const normalizedValue: number[] = useMemo(() => {
    const newValue = value ?? [];
    if (deepEqual(newValue, prevIdsRef.current)) {
      return prevIdsRef.current;
    }
    prevIdsRef.current = newValue;
    return newValue;
  }, [value]);

  const brandsMap = useMemo(() => {
    return brands.reduce(
      (acc, brand) => {
        acc[brand.value] = brand;
        return acc;
      },
      {} as Record<number, BrandOption>,
    );
  }, [brands]);

  const loadOptions = useCallback(
    async (inputValue: string) => {
      try {
        return await loadBrandsWithIdsDebounce({
          query: inputValue,
          ids: normalizedValue,
        });
      } catch (error) {
        console.error("Ошибка загрузки брендов:", error);
        return [];
      }
    },
    [loadBrandsWithIdsDebounce, normalizedValue],
  );

  useEffect(() => {
    loadBrands({
      query: "",
      ids: normalizedValue,
    });
  }, [loadBrands, normalizedValue]);

  const selectedOptions = useMemo(
    () =>
      normalizedValue.map((id) => ({
        label: brandsMap[id]?.label ?? "",
        value: id,
      })),
    [brandsMap, normalizedValue],
  );

  const onSelectChange = useCallback(
    (selected: MultiValue<BrandOption> | SingleValue<BrandOption>) => {
      if (!selected) {
        onChange(null);
        return;
      }

      if (isMultiValueSelect(selected)) {
        onChange(selected.map((s) => s.value) ?? null);
      } else {
        onChange([selected.value]);
      }
    },
    [onChange],
  );

  return (
    <Async<BrandOption, true>
      loadOptions={loadOptions}
      value={selectedOptions}
      onChange={onSelectChange}
      placeholder={placeholder}
      isLoading={isLoading}
      styles={SELECT_STYLES}
      loadingMessage={() => LOADING_MESSAGE}
      defaultOptions={brands}
      isMulti
    />
  );
};
